<template>
    <TransitionRoot
        as="template"
        :show="state.open"
    >
        <Dialog
            as="div"
            class="relative z-6000"
            :open="state.open === true"
            @close="dialogClose(state.overlayPreventClose)"
        >
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div
                    class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 scale-0"
                        enter-to="opacity-100 translate-y-0 scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 scale-0"
                    >
                        <DialogPanel
                            class="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl sm:p-6"
                        >
                            <div class="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                                <button
                                    type="button"
                                    class="cursor-pointer rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-hidden"
                                    @click="dialogClose()"
                                >
                                    <span class="sr-only">Close</span>
                                    <MdiClose
                                        class="h-6 w-6"
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                            <div class="sm:flex sm:items-start">
                                <div
                                    class="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-200 sm:mx-0 sm:h-10 sm:w-10"
                                >
                                    <MdiQrcodeEdit
                                        class="h-6 w-6 text-gray-600"
                                        aria-hidden="true"
                                    />
                                </div>
                                <div
                                    class="mt-3 text-center select-none sm:mt-0 sm:ml-4 sm:text-left"
                                >
                                    <DialogTitle
                                        as="h3"
                                        class="text-base leading-6 font-semibold text-gray-900 select-none"
                                    >
                                        {{ t('placeholderTypeImageCode') }}
                                    </DialogTitle>
                                    <div class="mt-2">
                                        <p class="text-sm text-gray-500">
                                            {{ t('imageCodeTypeDescription') }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div class="mt-5 gap-2 sm:mt-4 sm:flex">
                                <select
                                    id="placeholders"
                                    v-model="selectedImageCodeIndex"
                                    name="placeholders"
                                    class="w-full rounded-md border border-gray-300 bg-white font-medium shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                >
                                    <option
                                        v-for="(imageCodeType, index) in imageCodeTypes"
                                        :key="imageCodeType.code"
                                        class="font-medium"
                                        :value="index"
                                    >
                                        {{ imageCodeType.name }}
                                    </option>
                                </select>
                            </div>
                            <div class="mt-5 min-h-[300px] sm:mt-4">
                                <img
                                    :src="imagePreview.src"
                                    class="mx-auto"
                                />
                            </div>
                            <div class="mt-5 justify-end gap-2 sm:mt-4 sm:flex">
                                <ActionButton
                                    :value="t('buttonInsertImagePlaceholderData')"
                                    state="primary"
                                    :icon="MdiContentSave"
                                    @click="
                                        setPlaceholderModel();
                                        dialogClose();
                                    "
                                />
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { onMounted, reactive, ref, watch } from 'vue';
import ActionButton from '../ui/ActionButton.vue';
import {
    setPlaceholderRequiredKeys,
    getImageCodeTypes,
    transformGroup,
} from '../composables/useImageCodes';
import { getImageCodeImage } from '../composables/usePlaceholders';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';

import { MdiQrcodeEdit, MdiClose, MdiContentSave } from 'materialdesignicons-vue3/icons/';

defineExpose({
    name: 'ImageCodeWizzard',
});

const props = defineProps({
    state: {
        type: Object,
        default: () => {
            return {
                open: false,
                overlayPreventClose: false,
            };
        },
    },
    placeholder: {
        type: Object,
        default: () => {
            return {};
        },
    },
});

const state = props.state;
const placeholder = props.placeholder;
const imageCodeTypes = getImageCodeTypes();
const selectedImageCodeIndex = ref(0);
const imagePreview = reactive({ src: '' });
let imagePreviewObjectUrl = '';

const setPlaceholderModel = () => {
    setPlaceholderRequiredKeys(placeholder, imageCodeTypes[selectedImageCodeIndex.value]);
    transformGroup(placeholder, imageCodeTypes[selectedImageCodeIndex.value]);
    //console.log('setPlaceholderModel', placeholder);
    //state.open = false;
};

const resetPlaceholderKeys = () => {
    placeholder.model = [];
    placeholder.sample = [];
    placeholder.uiConfig = {};
};

const setImgSrc = async () => {
    const imageSrcData = await getImageCodeImage(placeholder);

    if (imagePreviewObjectUrl.length > 0) {
        URL.revokeObjectURL(imagePreviewObjectUrl);
        imagePreviewObjectUrl = '';
    }

    if (imageSrcData instanceof Blob) {
        imagePreviewObjectUrl = URL.createObjectURL(imageSrcData);
        imagePreview.src = imagePreviewObjectUrl;
        return;
    }

    imagePreview.src = '';
};

watch(selectedImageCodeIndex, () => {
    resetPlaceholderKeys();
    setPlaceholderModel();
    setImgSrc();
});

watch(state, (newVal) => {
    if (newVal.open === true) {
        resetPlaceholderKeys();
    }
});

onMounted(() => {
    if (state.open === true) {
        resetPlaceholderKeys();
        setPlaceholderModel();
        setImgSrc();
    }
});

const dialogClose = (preventClose = false) => {
    if (preventClose === false) {
        state.open = false;
    }
};
</script>
